import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { Language } from './languageStore';
import type { AIProgram } from '../api/gpt';
import { generateCallScript, generateAppealLetter } from '../api/gpt';
import { supabase } from '../lib/supabase';

export interface MatchCriteria {
  name: Record<string, string> | string;
  passed: boolean;
  value?: Record<string, string> | string;
}

export interface MatchResult {
  id: number;
  name: Record<string, string> | string;
  description: Record<string, string> | string;
  type: string;
  matchPercentage: number;
  matchCriteria: MatchCriteria[];
  aiProgram: AIProgram;
}

interface MatchStore {
  matchResults: MatchResult[];
  formData: any;
  setMatchResults: (results: MatchResult[]) => void;
  setFormData: (data: any) => void;
  clearResults: () => void;
  // Language-specific program cache
  programCache: Record<string, Record<string, AIProgram[]>>;
  setProgramCache: (zipCode: string, need: string, language: string, programs: AIProgram[]) => void;
  getProgramCache: (zipCode: string, need: string, language: string) => AIProgram[] | null;
  // Regenerate content for new language
  regenerateContentForLanguage: (newLanguage: Language) => Promise<void>;
}

export const useMatchStore = create<MatchStore>()(
  persist(
    (set, get) => ({
      matchResults: [],
      formData: {},
      programCache: {},
      
      setMatchResults: (results) => set({ matchResults: results }),
      setFormData: (data) => set({ formData: data }),
      clearResults: () => set({ matchResults: [] }),
      
      setProgramCache: (zipCode, need, language, programs) => {
        const cacheKey = `${zipCode}-${need}`;
        set((state) => ({
          programCache: {
            ...state.programCache,
            [cacheKey]: {
              ...state.programCache[cacheKey],
              [language]: programs
            }
          }
        }));
      },
      
      getProgramCache: (zipCode, need, language) => {
        const cacheKey = `${zipCode}-${need}`;
        const state = get();
        return state.programCache[cacheKey]?.[language] || null;
      },

      regenerateContentForLanguage: async (newLanguage: Language) => {
        const state = get();
        const { matchResults } = state;
        
        if (!matchResults || matchResults.length === 0) return;

        // Regenerate AI content for each match result
        const updatedResults = await Promise.all(
          matchResults.map(async (result) => {
            const aiProgram = result.aiProgram;
            
            try {
              // Regenerate program content in the new language
              const { data, error } = await supabase.functions.invoke('ai-proxy', {
                body: {
                  type: 'program_recommendations',
                  data: {
                    zipCode: aiProgram.address.zip,
                    need: result.type as 'rent' | 'food' | 'shelter' | 'legal' | 'jobs',
                    radius: 25,
                    language: newLanguage
                  }
                }
              });

              if (error) {
                throw new Error(`Failed to get program recommendations: ${error.message}`);
              }

              if (!data || !data.programs) {
                throw new Error('No programs returned from AI service');
              }

              const regeneratedPrograms = data.programs;
              
              // Find the matching program by ID or use the first one
              const regeneratedProgram = regeneratedPrograms.find(p => p.id === aiProgram.id) || regeneratedPrograms[0];
              
              if (regeneratedProgram) {
                const updatedAIProgram = {
                  ...aiProgram,
                  name: regeneratedProgram.name,
                  description: regeneratedProgram.description,
                  eligibility: regeneratedProgram.eligibility,
                  services: regeneratedProgram.services
                };

                return {
                  ...result,
                  name: regeneratedProgram.name,
                  description: regeneratedProgram.description,
                  aiProgram: updatedAIProgram
                };
              }
            } catch (error) {
              console.error('Error regenerating content for language change:', error);
            }
            
            // Return original result if regeneration fails
            return result;
          })
        );

        set({ matchResults: updatedResults });
      }
    }),
    {
      name: 'match-storage',
    }
  )
);